import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { Card, CardContent } from '@/components/ui/card';
import { ChartCardHeader } from '@/components/metrics/chart-card-header';
import { formatNumber } from '@/components/metrics/formatters';
import {
  COMPLETED_ORDERS_COLOR,
  NOT_COMPLETED_ORDERS_COLOR,
  OrdersCompletionDonut,
} from '@/components/orders-completion-donut';
import {
  isOrdersCompletionClickable,
  type OrdersCompletionSegment,
} from '@/lib/orders-drilldown';

interface OrdersCompletionCardProps {
  completedCount: number;
  notCompletedCount: number;
  isLoading: boolean;
  /** When provided, the slices and the legend rows are clickable. */
  onSegmentClick?: (segment: OrdersCompletionSegment) => void;
}

export function OrdersCompletionCard({
  completedCount,
  notCompletedCount,
  isLoading,
  onSegmentClick,
}: OrdersCompletionCardProps) {
  const { LL, locale } = useI18nContext();

  const totalOrders = completedCount + notCompletedCount;
  const completedPercentage =
    totalOrders > 0 ? Math.round((completedCount / totalOrders) * 100) : null;

  const isClickable = isOrdersCompletionClickable({
    completedCount,
    notCompletedCount,
    enabled: onSegmentClick !== undefined,
  });

  const legendItems: Array<{
    key: OrdersCompletionSegment;
    color: string;
    icon: LucideIcon;
    label: string;
    description: string;
    count: number;
  }> = [
    {
      key: 'completed',
      color: COMPLETED_ORDERS_COLOR,
      icon: CheckCircle2,
      label: LL.metricsPage.ordersCompletion.completed.label(),
      description: LL.metricsPage.ordersCompletion.completed.description(),
      count: completedCount,
    },
    {
      key: 'notCompleted',
      color: NOT_COMPLETED_ORDERS_COLOR,
      icon: XCircle,
      label: LL.metricsPage.ordersCompletion.notCompleted.label(),
      description: LL.metricsPage.ordersCompletion.notCompleted.description(),
      count: notCompletedCount,
    },
  ];

  return (
    <Card>
      <ChartCardHeader
        title={LL.metricsPage.ordersCompletion.title()}
        helpKey="ordersCompletion"
      />
      <CardContent>
        {isLoading ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="flex w-full flex-wrap items-start justify-center gap-4">
            <div className="relative size-[200px] shrink-0">
              <OrdersCompletionDonut
                completedCount={completedCount}
                notCompletedCount={notCompletedCount}
                onSegmentClick={onSegmentClick}
              />
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold leading-none">
                  {completedPercentage != null
                    ? `${completedPercentage}%`
                    : '--'}
                </span>
                <span className="mt-1 text-xs text-muted-foreground">
                  {LL.metricsPage.ordersCompletion.centerLabel()}
                </span>
              </div>
            </div>

            <div className="flex min-w-[260px] flex-1 basis-[min(100%,260px)] flex-col gap-2">
              {legendItems.map((item, index) => {
                const Icon = item.icon;
                const rowClassName =
                  index === 0
                    ? 'flex flex-col gap-1'
                    : 'flex flex-col gap-1 border-t border-border pt-2';
                const rowContent = (
                  <>
                    <div className="flex min-w-0 flex-wrap items-start gap-x-2 gap-y-1">
                      <Icon
                        className="mt-0.5 size-4 shrink-0"
                        style={{ color: item.color }}
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1 text-sm font-semibold text-foreground break-words">
                        {item.label}
                      </span>
                      {totalOrders > 0 ? (
                        <span className="text-sm font-medium text-foreground">
                          {formatNumber(item.count, locale)}
                        </span>
                      ) : null}
                    </div>
                    <p className="pl-6 text-xs text-muted-foreground break-words">
                      {item.description}
                    </p>
                  </>
                );
                return isClickable && onSegmentClick ? (
                  <button
                    key={item.key}
                    type="button"
                    className={`${rowClassName} w-full cursor-pointer rounded-md text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
                    aria-label={LL.metricsPage.repurchaseDrilldown.viewOrdersAriaLabel(
                      { label: item.label }
                    )}
                    onClick={() => onSegmentClick(item.key)}
                  >
                    {rowContent}
                  </button>
                ) : (
                  <div key={item.key} className={rowClassName}>
                    {rowContent}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
